import { observable, action, makeObservable } from 'mobx';
import { IRestoreItems } from 'types';
import { toast, toastTypeEnum } from 'common/utils';
import { getApiErrorMessage } from 'services/utils';

import {
  getAllDeletedItems,
  restoreItems,
  deleteItemsForever
} from 'services/recycle';

export type IInitialState = {
  data: {
    tabel: 'TRIM' | 'SUBMODEL' | 'PART';
    submodels: [];
    trims: [];
    parts: [];
    limit: number;
    search: string;
    offset: number;
    total: number;
  };
  ui: {
    recyclerLoading: boolean;
  };
};

export const initialState: IInitialState = {
  data: {
    tabel: 'PART',
    submodels: [],
    trims: [],
    parts: [],
    search: '',
    limit: 10,
    offset: 1,
    total: 0
  },
  ui: {
    recyclerLoading: false
  }
};

export class RecycleStore {
  state: IInitialState = initialState;

  getAllDeletedItems = async (
    search = '',
    limit = 10,
    offset = 1,
    type: 'TRIM' | 'SUBMODEL' | 'PART'
  ) => {
    try {
      this.setState({
        ui: { ...this.state.ui, recyclerLoading: true },
        data: { ...this.state.data, tabel: type }
      });
      const { data } = await getAllDeletedItems({
        type,
        ...{ search: search, limit },
        offset: (offset - 1) * limit
      });
      this.setState({
        data: {
          ...this.state.data,
          ...(type === 'PART' ? { parts: data.rows } : {}),
          ...(type === 'SUBMODEL' ? { submodels: data.rows } : {}),
          ...(type === 'TRIM' ? { trims: data.rows } : {}),
          total: data.count,
          limit: limit,
          offset: offset
        }
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, recyclerLoading: false } });
    }
  };

  restoreItems = async (data: IRestoreItems) => {
    try {
      this.setState({ ui: { ...this.state.ui, recyclerLoading: true } });
      await restoreItems(data);
      const {
        data: { limit, search, offset }
      } = this.state;
      await this.getAllDeletedItems(search, limit, offset, data.type);
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, recyclerLoading: false } });
    }
  };

  deleteItemForever = async (data: IRestoreItems) => {
    try {
      this.setState({ ui: { ...this.state.ui, recyclerLoading: true } });
      await deleteItemsForever(data);
      const {
        data: { limit, search, offset }
      } = this.state;
      await this.getAllDeletedItems(search, limit, offset, data.type);
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, recyclerLoading: false } });
    }
  };

  setState = (params: Partial<IInitialState>) => {
    const { state } = this;
    this.state = {
      ...state,
      ...params
    };
    /* use this space to save/sync state with localStorage */
  };

  reset = () => {
    this.state = initialState;
    /* use this space to reset localStorage in case if you're persisting this state */
  };

  constructor() {
    makeObservable(this, {
      state: observable,
      getAllDeletedItems: action,
      restoreItems: action,
      deleteItemForever: action,
      setState: action,
      reset: action
    });
  }
}
